import React from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
  Platform,
  Dimensions,
} from 'react-native';
import { colors, typography } from '../../theme';
import { designTokens, componentStyles, createComponentStyle } from '../../theme/design-system';
import { useTypography } from '../../hooks/useTypography';
import { useThemedStyles } from '../../hooks/useThemedStyles';

// Helper function for responsive font sizing
const getResponsiveFontSize = () => {
  const { width: screenWidth } = Dimensions.get('window');
  
  if (screenWidth >= designTokens.breakpoints.desktop) {
    return typography.fontSize.base; // Use base size instead of sm
  } else if (screenWidth >= designTokens.breakpoints.tablet) {
    return typography.fontSize.base;
  }
  return typography.fontSize.base;
};

export interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'gradient';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
  containerStyle?: ViewStyle;
}

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  style,
  textStyle,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const [screenWidth] = React.useState(Dimensions.get('window').width);
  const isDisabled = disabled || loading;
  const typo = useTypography();

  // Get button styles based on variant and size
  const getButtonStyles = () => {
    const baseStyles = {
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      flexDirection: 'row' as const,
      borderRadius: 8,
      overflow: 'hidden' as const,
    };

    // Size-based styles
    const sizeStyles = {
      sm: { height: 36, paddingHorizontal: 12, paddingVertical: 8 },
      md: { height: 44, paddingHorizontal: 16, paddingVertical: 10 },
      lg: { height: 52, paddingHorizontal: 20, paddingVertical: 12 },
      xl: { height: 60, paddingHorizontal: 24, paddingVertical: 14 },
    };

    // Variant-based styles
    const variantStyles = {
      primary: {
        backgroundColor: colors.primary,
        shadowColor: colors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
      secondary: {
        backgroundColor: colors.secondary,
        shadowColor: colors.secondary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
      accent: {
        backgroundColor: colors.accent,
        shadowColor: colors.accent,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
      outline: {
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderColor: colors.primary,
        shadowColor: colors.primary,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 4,
      },
      ghost: {
        backgroundColor: 'transparent',
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
      },
      gradient: {
        backgroundColor: colors.primary,
        shadowColor: colors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
    };

    return {
      ...baseStyles,
      ...sizeStyles[size],
      ...variantStyles[variant],
      ...(isDisabled && { opacity: 0.6 }),
    };
  };

  // Get text styles based on variant
  const getTextStyles = () => {
    const baseTextStyles = {
      fontSize: size === 'sm' ? 14 : size === 'md' ? 16 : size === 'lg' ? 18 : 20,
      fontWeight: '600' as const,
      textAlign: 'center' as const,
    };

    const variantTextStyles = {
      primary: { color: colors.white },
      secondary: { color: colors.white },
      accent: { color: colors.textDark },
      outline: { color: colors.primary },
      ghost: { color: colors.primary },
      gradient: { color: colors.white },
    };

    return {
      ...baseTextStyles,
      ...variantTextStyles[variant],
      ...(isDisabled && { opacity: 0.6 }),
    };
  };

  return (
    <View style={[styles.container, fullWidth && styles.fullWidth, containerStyle]}>
      <TouchableOpacity
        style={[getButtonStyles(), fullWidth && { width: '100%' }, style]}
        onPress={onPress}
        disabled={isDisabled}
        activeOpacity={0.85}
      >
        {loading ? (
          <ActivityIndicator
            size="small"
            color={variant === 'outline' || variant === 'ghost' ? colors.primary : colors.white}
          />
        ) : (
          <>
            {/* Left Icon */}
            {leftIcon && (
              <View style={styles.leftIcon}>
                {leftIcon}
              </View>
            )}

            {/* Button Text */}
            <Text style={[getTextStyles(), { fontFamily: typo.fontFamily.semibold }, textStyle]}>
              {title}
            </Text>

            {/* Right Icon */}
            {rightIcon && (
              <View style={styles.rightIcon}>
                {rightIcon}
              </View>
            )}
          </>
        )}
      </TouchableOpacity>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    alignSelf: 'flex-start',
  },
  fullWidth: {
    alignSelf: 'stretch',
  },
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.lg,
    flexDirection: 'row',
    position: 'relative',
    overflow: 'hidden',
  },
  text: {
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
    letterSpacing: 0.5,
    flexShrink: 1,
    // Ensure text is visible
    color: colors.textDark,
  },
  leftIcon: {
    marginRight: designTokens.spacing.sm,
  },
  rightIcon: {
    marginLeft: designTokens.spacing.sm,
  },
});
